<template>
  <v-container fluid>
    <v-card class="pa-3 mb-3">
      <v-layout row wrap align-center>
        <v-flex xs12 sm8 class="px-2">
          <v-text-field v-model="search" :label="$t('walletsPage.searchLabel')" append-icon="search"
            :loading="loading" @keyup.enter="fetch" @click:append="fetch" />
        </v-flex>
        <v-flex xs12 sm4 class="px-2 text-xs-end">
          <v-btn flat color="primary" :loading="loading" @click="fetch">{{ $t("walletsPage.refresh") }}</v-btn>
        </v-flex>
      </v-layout>
    </v-card>

    <!-- Pending cash top-up requests (from transfer centers / riders) -->
    <v-card class="pa-3 mb-3" v-if="pendingRequests.length">
      <div class="title mb-2">{{ $t("walletsPage.pendingRequestsTitle") }}</div>
      <v-data-table :headers="reqHeaders" :items="pendingRequests" hide-actions class="elevation-0">
        <template slot="items" slot-scope="props">
          <td>
            {{ props.item.passenger && props.item.passenger.full_name }}
            <span class="grey--text">({{ props.item.passenger && props.item.passenger.public_rider_code }})</span>
          </td>
          <td><strong>{{ formatNum(props.item.amount) }} {{ $t("walletsPage.syp") }}</strong></td>
          <td>{{ props.item.channel || '—' }}</td>
          <td>{{ props.item.note || '—' }}</td>
          <td>{{ formatDate(props.item.created_at) }}</td>
          <td>
            <v-btn small flat color="green" :loading="saving" @click="approveRequest(props.item)">{{ $t("walletsPage.approve") }}</v-btn>
            <v-btn small flat color="red" @click="rejectRequest(props.item)">{{ $t("walletsPage.reject") }}</v-btn>
          </td>
        </template>
      </v-data-table>
    </v-card>

    <v-card>
      <v-data-table :headers="headers" :items="wallets" :loading="loading" :total-items="total"
        :pagination.sync="pagination" class="elevation-0">
        <template slot="items" slot-scope="props">
          <td>{{ props.item.passenger_id }}</td>
          <td>{{ props.item.full_name || '—' }}</td>
          <td>{{ props.item.username || '—' }}</td>
          <td>{{ props.item.public_rider_code || '—' }}</td>
          <td><strong :style="{ color: props.item.balance < 0 ? 'red' : 'inherit' }">{{ formatNum(props.item.balance) }} {{ $t("walletsPage.syp") }}</strong></td>
          <td>
            <v-btn small flat color="green" @click="openCredit(props.item)">{{ $t('wallet.cashTopup') }}</v-btn>
            <v-btn small flat color="primary" @click="openAdjust(props.item)">{{ $t('wallet.manualAdjust') }}</v-btn>
            <v-btn small flat @click="openTransactions(props.item)">{{ $t("walletsPage.log") }}</v-btn>
          </td>
        </template>
        <template slot="no-data">
          <div class="text-xs-center pa-4 grey--text">{{ $t("walletsPage.noWallets") }}</div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Direct cash credit (transfer center / admin) -->
    <v-dialog v-model="creditDialog" max-width="420">
      <v-card class="pa-3" v-if="activeWallet">
        <div class="title mb-2">{{ $t('wallet.cashTopup') }} — {{ activeWallet.full_name }}</div>
        <div class="grey--text mb-2">{{ $t("walletsPage.balance") }}: {{ formatNum(activeWallet.balance) }} {{ $t("walletsPage.syp") }}</div>
        <div class="grey--text mb-2" style="font-size:12px">{{ $t('wallet.cashTopupHint') }}</div>
        <v-text-field v-model.number="creditAmount" type="number" :label="$t('wallet.amount')" />
        <v-select v-model="creditChannel" :items="channels" :label="$t('wallet.channel')" :hint="$t('wallet.channelHint')" persistent-hint />
        <v-text-field v-model="creditNote" :label="$t('walletsPage.noteLabel')" />
        <div class="text-xs-end">
          <v-btn flat @click="creditDialog = false">{{ $t("walletsPage.cancel") }}</v-btn>
          <v-btn color="green" dark :loading="saving" @click="doCredit">{{ $t("walletsPage.addBalance") }}</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Manual adjustment (audited, reason required) -->
    <v-dialog v-model="adjustDialog" max-width="440">
      <v-card class="pa-3" v-if="activeWallet">
        <div class="title mb-2">{{ $t('wallet.manualAdjust') }} — {{ activeWallet.full_name }}</div>
        <div class="grey--text mb-2">{{ $t("walletsPage.balance") }}: {{ formatNum(activeWallet.balance) }} {{ $t("walletsPage.syp") }}</div>
        <div class="grey--text mb-2" style="font-size:12px">{{ $t('wallet.manualAdjustHint') }}</div>
        <v-select v-model="adjustDirection" :items="directions" :label="$t('wallet.direction')" />
        <v-text-field v-model.number="adjustAmount" type="number" :label="$t('wallet.amount')" />
        <v-text-field v-model="adjustReason" :label="$t('wallet.reason')" />
        <div class="text-xs-end">
          <v-btn flat @click="adjustDialog = false">{{ $t("walletsPage.cancel") }}</v-btn>
          <v-btn color="primary" :loading="saving" @click="doAdjust">{{ $t("walletsPage.apply") }}</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Statement -->
    <v-dialog v-model="txDialog" max-width="760">
      <v-card class="pa-3" v-if="activeWallet">
        <div class="title mb-2">{{ activeWallet.full_name }} — {{ $t("walletsPage.statementTitle") }}</div>
        <v-data-table :headers="txHeaders" :items="transactions" :loading="txLoading" hide-actions class="elevation-0">
          <template slot="items" slot-scope="props">
            <td>{{ formatDate(props.item.created_at) }}</td>
            <td>{{ props.item.type }}</td>
            <td :style="{ color: props.item.direction === 'DEBIT' ? 'red' : 'green' }">
              {{ props.item.direction === 'DEBIT' ? '-' : '+' }}{{ formatNum(props.item.amount) }}
            </td>
            <td>{{ formatNum(props.item.balance_after) }}</td>
            <td>{{ props.item.booking_id || '—' }}</td>
            <td>{{ props.item.reason || '—' }}</td>
          </template>
          <template slot="no-data"><div class="text-xs-center pa-3 grey--text">{{ $t("walletsPage.noTransactions") }}</div></template>
        </v-data-table>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { formatCompanyTime } from "@/util/companyTime";

export default {
  name: "PassengerWallets",
  title() {
    return this.$t("walletsPage.pageTitle");
  },
  data() {
    return {
      search: "",
      loading: false,
      saving: false,
      wallets: [],
      total: 0,
      pagination: { rowsPerPage: 20, page: 1 },
      pendingRequests: [],
      activeWallet: null,
      creditDialog: false,
      creditAmount: null,
      creditChannel: "ADMIN",
      creditNote: "",
      adjustDialog: false,
      adjustAmount: null,
      adjustDirection: "CREDIT",
      adjustReason: "",
      txDialog: false,
      transactions: [],
      txLoading: false,
    };
  },
  computed: {
    reqHeaders() {
      return [
        { text: this.$t("walletsPage.rider"), value: "passenger", sortable: false },
        { text: this.$t("walletsPage.amount"), value: "amount", sortable: false },
        { text: this.$t("walletsPage.channel"), value: "channel", sortable: false },
        { text: this.$t("walletsPage.note"), value: "note", sortable: false },
        { text: this.$t("walletsPage.date"), value: "created_at", sortable: false },
        { text: "", value: "actions", sortable: false },
      ];
    },
    headers() {
      return [
        { text: this.$t("walletsPage.riderNo"), value: "passenger_id", sortable: false },
        { text: this.$t("walletsPage.name"), value: "full_name", sortable: false },
        { text: this.$t("walletsPage.phone"), value: "username", sortable: false },
        { text: this.$t("walletsPage.riderCode"), value: "public_rider_code", sortable: false },
        { text: this.$t("walletsPage.balance"), value: "balance", sortable: false },
        { text: "", value: "actions", sortable: false },
      ];
    },
    txHeaders() {
      return [
        { text: this.$t("walletsPage.date"), value: "created_at", sortable: false },
        { text: this.$t("walletsPage.type"), value: "type", sortable: false },
        { text: this.$t("walletsPage.amount"), value: "amount", sortable: false },
        { text: this.$t("walletsPage.balance"), value: "balance_after", sortable: false },
        { text: this.$t("walletsPage.booking"), value: "booking_id", sortable: false },
        { text: this.$t("walletsPage.reason"), value: "reason", sortable: false },
      ];
    },
    // Values stay ADMIN/TRANSFER_CENTER and CREDIT/DEBIT (sent to the backend);
    // only the displayed text is localized (Arabic in the ar locale).
    channels() {
      return [
        { text: this.$t("wallet.channelAdmin"), value: "ADMIN" },
        { text: this.$t("wallet.channelCenter"), value: "TRANSFER_CENTER" },
      ];
    },
    directions() {
      return [
        { text: this.$t("wallet.add"), value: "CREDIT" },
        { text: this.$t("wallet.remove"), value: "DEBIT" },
      ];
    },
  },
  watch: {
    pagination: { handler() { this.fetch(); }, deep: true },
  },
  mounted() { this.fetch(); this.loadPendingRequests(); },
  methods: {
    formatNum(v) { return v == null ? "—" : this.formatMoney(v); },
    // Final-version doc 2026-10-05: wallet rows arrive as ISO "Z"; print them
    // on the company clock (UTC+3) instead of the PC's zone and locale.
    formatDate(d) { return formatCompanyTime(d, "YYYY-MM-DD HH:mm:ss", "—"); },
    toast(msg, color) { window.getApp.$emit("SHOW_TOAST", msg, color); },
    errMsg(e) { return (e.response && e.response.data && e.response.data.message) || this.$t("walletsPage.failed"); },
    fetch() {
      this.loading = true;
      this.axios.get("wallets", {
        params: { search: this.search, perPage: this.pagination.rowsPerPage, page: this.pagination.page },
      }).then(({ data }) => {
        const p = data.data || {};
        this.wallets = p.data || [];
        this.total = p.total || this.wallets.length;
      }).catch(() => this.toast(this.$t("walletsPage.failedToLoad"), "red"))
        .finally(() => (this.loading = false));
    },
    loadPendingRequests() {
      this.axios.get("wallet-topup-requests", { params: { status: "PENDING", perPage: 100 } })
        .then(({ data }) => { this.pendingRequests = (data.data && data.data.data) || []; })
        .catch(() => { this.pendingRequests = []; });
    },
    approveRequest(req) {
      this.saving = true;
      this.axios.post(`wallet-topup-requests/${req.id}/approve`)
        .then(() => { this.toast(this.$t("walletsPage.approvedCredited"), "green"); this.loadPendingRequests(); this.fetch(); })
        .catch((e) => this.toast(this.errMsg(e), "red"))
        .finally(() => (this.saving = false));
    },
    rejectRequest(req) {
      this.axios.post(`wallet-topup-requests/${req.id}/reject`)
        .then(() => { this.toast(this.$t("walletsPage.rejectedToast"), "green"); this.loadPendingRequests(); })
        .catch((e) => this.toast(this.errMsg(e), "red"));
    },
    openCredit(wallet) {
      this.activeWallet = wallet;
      this.creditAmount = null;
      this.creditChannel = "ADMIN";
      this.creditNote = "";
      this.creditDialog = true;
    },
    doCredit() {
      if (!this.creditAmount || this.creditAmount <= 0) { this.toast(this.$t("walletsPage.enterAmount"), "red"); return; }
      this.saving = true;
      this.axios.post(`wallets/${this.activeWallet.passenger_id}/credit`, {
        amount: this.toStoredAmount(this.creditAmount),
        channel: this.creditChannel,
        note: this.creditNote,
      }).then(() => { this.toast(this.$t("walletsPage.balanceAdded"), "green"); this.creditDialog = false; this.fetch(); })
        .catch((e) => this.toast(this.errMsg(e), "red"))
        .finally(() => (this.saving = false));
    },
    openAdjust(wallet) {
      this.activeWallet = wallet;
      this.adjustAmount = null;
      this.adjustDirection = "CREDIT";
      this.adjustReason = "";
      this.adjustDialog = true;
    },
    doAdjust() {
      if (!this.adjustAmount || this.adjustAmount <= 0) { this.toast(this.$t("walletsPage.enterAmount"), "red"); return; }
      if (!this.adjustReason || !this.adjustReason.trim()) { this.toast(this.$t("walletsPage.reasonRequired"), "red"); return; }
      this.saving = true;
      this.axios.post(`wallets/${this.activeWallet.passenger_id}/adjust`, {
        amount: this.toStoredAmount(this.adjustAmount),
        direction: this.adjustDirection,
        reason: this.adjustReason,
      }).then(() => { this.toast(this.$t("walletsPage.adjusted"), "green"); this.adjustDialog = false; this.fetch(); })
        .catch((e) => this.toast(this.errMsg(e), "red"))
        .finally(() => (this.saving = false));
    },
    openTransactions(wallet) {
      this.activeWallet = wallet;
      this.transactions = [];
      this.txDialog = true;
      this.txLoading = true;
      this.axios.get(`wallets/${wallet.passenger_id}/transactions`, { params: { perPage: 50 } })
        .then(({ data }) => { this.transactions = (data.data && data.data.data) || []; })
        .catch(() => {})
        .finally(() => (this.txLoading = false));
    },
  },
};
</script>
